
import { useEffect } from 'react';
import { trackPageView, recordUserSession, updateSessionOnLogout } from '@/services/analyticsService';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useAnalytics = () => {
  // Wrap in try-catch in case it's used outside Router context
  let location = null;
  try {
    const { useLocation } = require('react-router-dom');
    location = useLocation();
  } catch (error) {
    console.error('useAnalytics hook used outside Router context:', error);
    return;
  }
  
  // Track page views when location changes
  useEffect(() => {
    if (location) {
      try {
        trackPageView(location.pathname);
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    }
  }, [location?.pathname]);
  
  // Record user session on initial load
  useEffect(() => {
    try {
      recordUserSession();
      
      // Update session on logout
      const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_OUT') {
          try {
            updateSessionOnLogout();
          } catch (error) {
            console.error('Error updating session on logout:', error);
          }
        }
      });
      
      return () => {
        authListener.subscription.unsubscribe();
      };
    } catch (error) {
      console.error('Error in analytics initialization:', error);
    }
  }, []);
};

export const useIsAdmin = () => {
  return useQuery({
    queryKey: ['isAdmin'],
    queryFn: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) return false;
        
        const { data, error } = await supabase
          .from('user_roles')
          .select('role_id')
          .eq('user_id', session.user.id);
        
        if (error) {
          console.error('Error checking admin status:', error);
          return false;
        }
        
        if (!data || data.length === 0) return false;
        
        // Check if any of the roles is the admin role
        const { data: adminRole, error: adminError } = await supabase
          .from('roles')
          .select('id')
          .eq('name', 'Administrator')
          .single();
          
        if (adminError || !adminRole) return false;
        
        return data.some(role => role.role_id === adminRole.id);
      } catch (error) {
        console.error('Error in admin check:', error);
        return false;
      }
    },
    staleTime: 60000, // 1 minute
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
